import CloudConnectivityView from '@app/pages/org/topology/CloudConnectivityView';
import type { TopologyV2Graphschema } from '@app/types/sdk';

import PathRelatedViewRenderer, { graphUsesPathRelatedViewRenderer } from './PathRelatedViewRenderer';
import TopologyCanvas, { type TopologyCanvasProps } from './TopologyCanvas';

type TopologyGraphRendererProps = Omit<TopologyCanvasProps, 'topology'> & {
  topology: TopologyV2Graphschema | null;
};

const asRecord = (value: unknown): Record<string, unknown> | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }
  return value as Record<string, unknown>;
};

const getString = (value: unknown): string | undefined => (typeof value === 'string' ? value : undefined);

export const graphUsesCloudConnectivityRenderer = (graph: TopologyV2Graphschema | null | undefined): boolean => {
  const presentation = asRecord(asRecord(graph?.meta)?.presentation);
  return (getString(presentation?.renderer) ?? '') === 'cloud_connectivity';
};

export const topologyGraphRendererKind = (graph: TopologyV2Graphschema | null | undefined): 'cloud_connectivity' | 'path_related_view' | 'canvas' => {
  if (graph && graphUsesCloudConnectivityRenderer(graph)) {
    return 'cloud_connectivity';
  }
  if (graph && graphUsesPathRelatedViewRenderer(graph)) {
    return 'path_related_view';
  }
  return 'canvas';
};

const noopNodeHandler = () => undefined;
const noopEdgeHandler = () => undefined;
const noopPaneHandler = () => undefined;
const emptySelectedIds = new Set<string>();

export const TopologyGraphRenderer = ({
  topology,
  selectedNodeIds = emptySelectedIds,
  selectedEdgeIds = emptySelectedIds,
  onNodeSelect = noopNodeHandler,
  onEdgeSelect = noopEdgeHandler,
  onPaneClick = noopPaneHandler,
  ...canvasProps
}: TopologyGraphRendererProps) => {
  const rendererKind = topologyGraphRendererKind(topology);
  if (rendererKind === 'cloud_connectivity' && topology) {
    return (
      <CloudConnectivityView
        graph={topology}
        selectedNodeIds={selectedNodeIds}
        selectedEdgeIds={selectedEdgeIds}
        onNodeSelect={onNodeSelect}
        onEdgeSelect={onEdgeSelect}
        onPaneClick={onPaneClick}
      />
    );
  }

  if (rendererKind === 'path_related_view' && topology) {
    return (
      <PathRelatedViewRenderer
        {...canvasProps}
        topology={topology}
        selectedNodeIds={selectedNodeIds}
        selectedEdgeIds={selectedEdgeIds}
        onNodeSelect={onNodeSelect}
        onEdgeSelect={onEdgeSelect}
        onPaneClick={onPaneClick}
      />
    );
  }

  return (
    <TopologyCanvas
      {...canvasProps}
      topology={topology}
      selectedNodeIds={selectedNodeIds}
      selectedEdgeIds={selectedEdgeIds}
      onNodeSelect={onNodeSelect}
      onEdgeSelect={onEdgeSelect}
      onPaneClick={onPaneClick}
    />
  );
};

export default TopologyGraphRenderer;
